import { FC, useMemo } from 'react';
import { MoveLeft, MoveRight } from 'lucide-react';
import type { QuestionWithStatus } from './ExamQuestionsContainer';

const FIRST_QUESTION_INDEX = 0;
const NAVIGATION_STEP = 1;

interface QuestionNavigationProps {
  filteredQuestions: QuestionWithStatus[];
  activeQuestionId: number | null;
  onQuestionSelect: (questionId: number) => void;
}

export const QuestionNavigation: FC<QuestionNavigationProps> = ({
  filteredQuestions,
  activeQuestionId,
  onQuestionSelect,
}) => {
  const navigationQuestions = useMemo(
    () =>
      filteredQuestions.map((q) => ({
        id: q.id,
        isCorrect: q.isCorrect,
        originalIndex: q.originalIndex,
      })),
    [filteredQuestions]
  );

  const activeIndex = navigationQuestions.findIndex((q) => q.id === activeQuestionId);
  const canGoPrev = activeIndex > FIRST_QUESTION_INDEX;
  const canGoNext = activeIndex < navigationQuestions.length - NAVIGATION_STEP;

  const handlePrev = () => {
    if (canGoPrev) {
      onQuestionSelect(navigationQuestions[activeIndex - NAVIGATION_STEP].id);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onQuestionSelect(navigationQuestions[activeIndex + NAVIGATION_STEP].id);
    }
  };

  return (
    <div className="flex items-center gap-4.5 overflow-x-auto pb-2">
      <button
        type="button"
        onClick={handlePrev}
        disabled={!canGoPrev}
        className="shrink-0 w-9.5 h-9.5 flex items-center justify-center rounded! bg-(--neutral-white-NW1000) disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:bg-(--neutral-black-NB100) transition-colors"
      >
        <MoveLeft size={18} className="text-(--neutral-white-NW600)" />
      </button>

      <div className="flex gap-2 flex-1 overflow-x-auto">
        {navigationQuestions.map((question) => {
          const isActive = question.id === activeQuestionId;
          const isCorrect = question.isCorrect;
          const borderColor = isCorrect
            ? 'border-[var(--success-S600)]!'
            : 'border-[var(--danger-D600)]!';
          const chooseActiveBgColor = isCorrect
            ? 'bg-[var(--success-S200)]!'
            : 'bg-[var(--danger-D200)]!';

          return (
            <button
              key={question.id}
              type="button"
              onClick={() => onQuestionSelect(question.id)}
              className={`shrink-0 w-10 h-8 flex items-center justify-center rounded border-b-2! ${borderColor} ${
                isActive ? chooseActiveBgColor : 'bg-(--neutral-white-NW1000)'
              } hover:bg-(--neutral-black-NB100) transition-colors [font-size:var(--fs-p4)]! text-(--neutral-black-NB700)!`}
            >
              {question.originalIndex + NAVIGATION_STEP}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleNext}
        disabled={!canGoNext}
        className="shrink-0 w-9.5 h-9.5 flex items-center justify-center rounded! bg-(--neutral-white-NW1000) disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:bg-(--neutral-black-NB100) transition-colors"
      >
        <MoveRight size={18} className="text-(--neutral-white-NW600)" />
      </button>
    </div>
  );
};
